<template>
    <div v-show="isLoaded" class="content-wrapper duty-selector">
        <div v-if="isAllowed" class="print-only">
            <table class="table">
                <thead>
                    <tr class="tbl-header">
                        <th>{{ $t('dutySelector.duty.number') }}</th>
                        <th>{{ $t('dutySelector.duty.name') }}</th>
                        <th>{{ $t('dutySelector.startTime') }}</th>
                        <th>{{ $t('dutySelector.duty.defaultByScheduler') }}</th>
                        <th>{{ $t('dutySelector.duty.currentByMARi') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="duty in duties" :key="duty.state">
                        <td>{{ duty.state }}</td>
                        <td>{{ duty.name }}</td>
                        <td>{{ duty.time }}</td>
                        <td><i v-if="duty.default" class="icon-checkmark"></i></td>
                        <td><i v-if="duty.override" class="icon-checkmark"></i></td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-if="isAllowed" class="no-print">
            <div class="label">{{ $t('dutySelector.label') }}</div>
            <div class="duties-wrapper">
                <label
                    v-for="duty in duties"
                    :key="duty.state"
                    :class="[
                        'duty',
                        { current: duty.override, selected: duty === selectedDuty, default: duty.default },
                    ]"
                    :for="`radio-duty-${duty.state}`"
                    :duty-shift-status="getDutyShiftStatus(duty)"
                >
                    <div v-if="duty.default" class="border"></div>
                    <input
                        :id="`radio-duty-${duty.state}`"
                        v-model="selectedDuty"
                        :value="duty"
                        type="radio"
                        name="duty-radio"
                        @change="radioClick(duty)"
                    />
                    <label :for="`radio-duty-${duty.state}`">
                        <div class="radio-icon"></div>
                        {{ duty.state }}
                    </label>
                    <div class="name">
                        {{ duty.name }}
                    </div>
                    <div class="time">{{ $t('dutySelector.startTime') + ': ' + duty.time }}</div>
                </label>
            </div>
        </div>
        <div v-else class="data-empty">
            <h3>{{ $t('dutySelector.noFunctionality') }}</h3>
        </div>
    </div>
</template>

<script type="text/babel">
import mappingState from './common/mappingState';
import { mapState, mapMutations, mapActions } from 'vuex';
import { SUCCESS } from '../constants';
import { ifIsNotCanceled, formatTime } from './common/helpers';

import {
    resourceAssignmentAreasHasSelector,
    resourceAssignmentAreasDutiesState,
    resourceDutySchedulerOverride,
    resourceDutySchedulerClear,
} from '../api/resources';
import { getCancelToken } from '../api';

export default {
    components: {},

    mixins: [mappingState],

    data() {
        return {
            duties: [],
            selectedDuty: null,
            selectedDutyCopy: null,
            isLoaded: false,
            isAllowed: false,
            cancelToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState({
            authToken: state => state.user.user.token,
            unsavedChanges: 'unsavedChanges',
        }),
    },

    created() {
        this.updateData(() => {
            this.setDuties();
            this.configButtons();
            return Promise.resolve();
        });
        this.showLoading(true);
        this.setDuties();
        this.configButtons();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
    },

    methods: {
        ...mapMutations({ updateData: 'SET_UPDATE_DATA' }),
        ...mapActions({
            setDutySelectorState: 'setDutySelectorState',
        }),
        configButtons() {
            this.removeButtons(['disableButton', 'saveButton', 'undoButton']);

            this.configButton({
                buttonName: 'saveButton',
                action: () => this.saveDutySelector(),
                disabled: () => !this.unsavedChanges || this.selectedDuty == null,
            });

            this.configButton({
                buttonName: 'undoButton',
                action: () => this.undo(),
                disabled: () => !this.unsavedChanges,
            });

            if (this.selectedDuty) {
                this.configButton({
                    buttonName: 'disableButton',
                    action: () => {
                        this.showLoading(true);
                        return resourceDutySchedulerClear({
                            method: 'post',
                            id: this.userAreaId,
                        })
                            .then(() => this.setDuties())
                            .then(() => this.removeButtons(['disableButton']))
                            .catch(ifIsNotCanceled());
                    },
                });
            }
        },
        radioClick() {
            this.setUnsavedChanges(true);
        },
        saveDutySelector() {
            this.confirmation({
                confirmTitle: this.$t('dutySelector.confirmationTitle'),
                confirmBody: this.$t('dutySelector.confirmationBody'),
                confirmOk: this.$t('common.save'),
            }).then(r => {
                if (r) {
                    this.showLoading(true);
                    resourceDutySchedulerOverride({
                        method: 'post',
                        id: this.userAreaId,
                        data: { state: this.selectedDuty.state },
                    })
                        .then(() => {
                            this.setDuties();
                            this.popup({
                                type: SUCCESS,
                                header: this.$t('common.success'),
                                text: this.$t('common.saved'),
                            });
                        })
                        .catch(ifIsNotCanceled());
                }
            });
        },
        setDuties() {
            this.isLoaded = false;
            return resourceAssignmentAreasHasSelector({
                id: this.userAreaId,
                cancelToken: this.cancelToken.token,
            })
                .then(res => {
                    if (!res.data.has_selector && res.data.have_duties) {
                        this.isAllowed = true;
                        return resourceAssignmentAreasDutiesState({
                            id: this.userAreaId,
                            cancelToken: this.cancelToken.token,
                        });
                    }

                    this.isAllowed = false;
                    this.isLoaded = true;
                    this.showLoading(false);
                    this.setUnsavedChanges(false);
                    this.duties = [];
                    this.selectedDuty = null;
                    this.selectedDutyCopy = null;
                    this.configButtons();
                    return Promise.reject('canceled');
                })
                .then(res => {
                    this.duties = res.data.duties
                        .filter(duty => !!duty.start_time)
                        .map(duty => ({
                            name: duty.duty_name,
                            state: duty.state,
                            default: duty.default,
                            override: duty.override,
                            time: formatTime(duty.start_time.substr(0, 5)),
                        }));

                    res.data.overriden = res.data.duties.some(elem => elem.override !== null);
                    this.setDutySelectorState(res.data);

                    this.duties.sort((a, b) => a.state - b.state);
                    this.selectedDuty = this.duties.find(duty => duty.override === true);
                    this.configButtons();
                    this.selectedDutyCopy = this.selectedDuty;
                    this.isLoaded = true;
                    this.showLoading(false);
                    this.setUnsavedChanges(false);
                })
                .catch(ifIsNotCanceled());
        },
        undo() {
            this.selectedDuty = this.selectedDutyCopy;
            this.$nextTick(() => {
                this.setUnsavedChanges(false);
            });
        },
        getDutyShiftStatus(duty) {
            return duty.override
                ? this.$t('dutySelector.duty.currentShift')
                : this.$t('dutySelector.duty.defaultShift');
        },
    },
};
</script>
